import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Route, Routes, useLocation } from 'react-router-dom';
import TwitterSearch from './components/TwitterSearch';
import Insights from './components/Insights';
import GenerativeMuseum from './components/GenerativeMuseum';
import InkQuoteManager from './components/InkQuoteManager';
import TabbedNavigation from './components/TabbedNavigation';
import ErrorBoundary from './ErrorBoundary';
import { Moon, Sun, Code, Building2, SquareDashedKanban } from 'lucide-react';
import InsightsIcon from './components/ui/InsightsIcon';
import { DevModeProvider, useDevMode } from './context/DevModeContext';
import { StagingTrayProvider } from './context/StagingTrayContext';
import { StagedEditProvider } from './context/StagedEditContext';
import { FavoritesProvider } from './context/FavoritesContext';
import { SkipCategoriesProvider, useSkipCategories } from './context/SkipCategoriesContext';
import { ToastProvider } from './context/ToastContext';
import { useApiSync } from './hooks/useApiSync';
import useScrollDirection from './hooks/useScrollDirection';
import StagingTrayIndicator from './components/ui/StagingTrayIndicator';
import StagingTrayBottomPanel from './components/ui/StagingTrayBottomPanel';
import ApiSyncStatus from './components/ui/ApiSyncStatus';
import ToastContainer from './components/ui/Toast';
import PWAInstallPrompt from './components/PWAInstallPrompt';
import PWAUpdatePrompt from './components/PWAUpdatePrompt';
import './App.css';

const TwitterIcon = ({ size = 24, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={color}>
    <path d="M23.954 4.569c-.885.392-1.83.656-2.825.775 1.014-.608 1.794-1.569 2.163-2.724-.951.564-2.005.974-3.127 1.195-.897-.959-2.178-1.559-3.594-1.559-2.717 0-4.92 2.203-4.92 4.917 0 .39.045.765.127 1.124-4.09-.205-7.719-2.165-10.148-5.144-.424.729-.666 1.576-.666 2.476 0 1.71.87 3.213 2.188 4.096-.807-.026-1.566-.248-2.23-.616v.062c0 2.385 1.693 4.374 3.946 4.827-.413.112-.849.171-1.296.171-.317 0-.626-.03-.927-.086.631 1.953 2.445 3.376 4.604 3.416-1.68 1.319-3.809 2.107-6.115 2.107-.398 0-.79-.023-1.175-.069 2.179 1.397 4.768 2.212 7.548 2.212 9.051 0 14-7.496 14-13.986 0-.21 0-.423-.015-.634.961-.695 1.8-1.562 2.46-2.549z" />
  </svg>
);

const AppContent = () => {
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('darkMode');
    return saved !== null ? JSON.parse(saved) : false;
  });
  const [allTweets, setAllTweets] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const location = useLocation();
  const { isDevMode } = useDevMode();
  const { initializeFromTweets } = useSkipCategories();
  const { isEnabled: apiSyncEnabled, isConnected, queuedCount, syncNow, triggerExport } = useApiSync();
  const { visible } = useScrollDirection();

  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
  };

  useEffect(() => {
    localStorage.setItem('darkMode', JSON.stringify(darkMode));
    if (darkMode) {
      document.documentElement.classList.add('dark');
      document.body.classList.add('dark-mode');
    } else {
      document.documentElement.classList.remove('dark');
      document.body.classList.remove('dark-mode');
    }
  }, [darkMode]);

  const decodeHTMLEntities = (text) => {
    if (!text) return '';
    const textArea = document.createElement('textarea');
    textArea.innerHTML = text;
    return textArea.value;
  };

  const loadTweets = useCallback(async () => {
    const LIKES_FILE = '/exports/likes/merged_tweets.json';
    const BOOKMARKS_FILE = '/exports/bookmarks/bookmarks.json';
    const USER_TWEETS_FILE = '/exports/user_tweets/20241015_user_tweets.json';
    const REDDIT_SAVES_FILE = '/exports/reddit/reddit-export.json';
    const ROAM_QUOTES_FILE = '/exports/roam/roam_quotes.json';

    const repairJSON = (jsonString) => {
      let repaired = jsonString;
      
      // Fix trailing commas in objects and arrays
      repaired = repaired.replace(/,(\s*[}\]])/g, '$1');
      
      // Fix missing commas between array elements
      repaired = repaired.replace(/}(\s*){/g, '},$1{');
      
      // Fix missing closing quotes in quoted phrases within strings
      // Pattern: \"phrase text"\n\n where the quote after "text" should be escaped
      repaired = repaired.replace(/\\"([^"\\]+)"\\n\\n/g, '\\"$1\\"\\n\\n');
      
      // Fix the specific case: "active break" should be "active break\"
      repaired = repaired.replace(/active break"\\n/g, 'active break\\"\\n');
      
      // More general fix: if we have a quote followed by \n\n and then another quote,
      // but the quote before \n\n is not escaped, escape it
      repaired = repaired.replace(/([^\\])"\\n\\n\\"/g, '$1\\"\\n\\n\\"');
      
      return repaired;
    };

    const parseJSONWithFallback = (text, name) => {
      try {
        return JSON.parse(text);
      } catch (e) {
        console.warn(`First JSON parse failed for ${name}, attempting repair...`);
        try {
          const repaired = repairJSON(text);
          return JSON.parse(repaired);
        } catch (repairError) {
          console.warn(`JSON repair failed for ${name}, attempting manual parsing...`);
          
          // For likes file, try to extract valid JSON objects manually
          if (name === 'likes') {
            try {
              // Find the array boundaries
              const startIndex = text.indexOf('[');
              const endIndex = text.lastIndexOf(']');
              
              if (startIndex !== -1 && endIndex !== -1) {
                const arrayContent = text.substring(startIndex + 1, endIndex);
                const objects = [];
                
                // Split by },{ pattern to separate objects
                const objectStrings = arrayContent.split(/},\s*{/);
                
                for (let i = 0; i < objectStrings.length; i++) {
                  let objStr = objectStrings[i].trim();
                  
                  // Add back the braces
                  if (i === 0) {
                    objStr = objStr.startsWith('{') ? objStr : '{' + objStr;
                  } else {
                    objStr = '{' + objStr;
                  }
                  
                  if (i === objectStrings.length - 1) {
                    objStr = objStr.endsWith('}') ? objStr : objStr + '}';
                  } else {
                    objStr = objStr + '}';
                  }
                  
                  try {
                    const obj = JSON.parse(objStr);
                    objects.push(obj);
                  } catch (parseError) {
                    console.warn(`Skipping malformed object ${i} in ${name}`);
                    continue;
                  }
                }
                
                if (process.env.REACT_APP_MODE === 'development') {
                  console.log(`Successfully parsed ${objects.length} objects from ${name} using fallback method`);
                }
                return objects;
              }
            } catch (fallbackError) {
              console.error(`Fallback parsing failed for ${name}:`, fallbackError);
            }
          }
          
          throw new Error(`All JSON parsing attempts failed for ${name}: ${e.message}`);
        }
      }
    };

    const fetchAndParse = async (url, name) => {
      try {
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const text = await response.text();
        return parseJSONWithFallback(text, name);
      } catch (error) {
        console.error(`Error fetching ${name}:`, error);
        throw error;
      }
    };

    try {
      const [likesData, bookmarksData, userTweetsData, redditData, roamData] = await Promise.all([
        fetchAndParse(LIKES_FILE, 'likes'),
        fetchAndParse(BOOKMARKS_FILE, 'bookmarks'),
        fetchAndParse(USER_TWEETS_FILE, 'user tweets'),
        fetchAndParse(REDDIT_SAVES_FILE, 'reddit saves'),
        fetchAndParse(ROAM_QUOTES_FILE, 'roam quotes')
      ]);
      
      if (process.env.REACT_APP_MODE === 'development') {
        console.log('Data loading results:', {
          likes: likesData?.length || 0,
          bookmarks: bookmarksData?.data?.length || bookmarksData?.length || 0,
          userTweets: userTweetsData?.length || 0,
          reddit: redditData?.length || 0,
          roam: roamData?.length || 0
        });
      }

      // if (!likesResponse.ok || !bookmarksResponse.ok || !userTweetsResponse.ok || !redditResponse.ok) {
      //   throw new Error(`Failed to load data: ${!likesResponse.ok ? 'likes ' : ''}${!bookmarksResponse.ok ? 'bookmarks ' : ''}${!userTweetsResponse.ok ? 'user tweets ' : ''}${!redditResponse.ok ? 'reddit' : ''}`);
      // }

      // const likesData = await likesResponse.json();
      // const bookmarksData = await bookmarksResponse.json();
      // const userTweetsData = await userTweetsResponse.json();      
      // const redditData = await redditResponse.json();

      const processedLikes = likesData
        .filter(tweet => !tweet.skip)
        .map(tweet => ({
          ...tweet,
          text: decodeHTMLEntities(tweet.full_text || tweet.text || ''),
          source: 'like',
          quote_status: tweet.quoted_status ? {
            ...tweet.quoted_status,
            text: decodeHTMLEntities(tweet.quoted_status.full_text || tweet.quoted_status.text || '')
          } : null,
          // Enhance media detection for new format
          has_media: tweet.medias?.length > 0 || tweet._raw_metadata?.has_media || false,
          // Preserve enhanced metadata for search and display
          enhanced_metadata: tweet._raw_metadata || null
        }));

      const processedBookmarks = bookmarksData.data.map(bookmark => ({
        id: bookmark.id,
        text: decodeHTMLEntities(bookmark.full_text || bookmark.text || ''),
        created_at: bookmark.created_at || bookmark.date,
        user: {
          name: bookmark.user.name,
          screen_name: bookmark.user.screen_name || bookmark.user.handle,
          profile_image_url: bookmark.user.profile_image_url || bookmark.user.profilePicUrl
        },
        url: bookmark.url,
        medias: bookmark.medias || bookmark.media,
        source: 'bookmark',
        retweet_count: bookmark.retweet_count,
        reply_count: bookmark.reply_count,
        quote_status: bookmark.quote_status ? {
          ...bookmark.quote_status,
          text: decodeHTMLEntities(bookmark.quote_status.text)
        } : null
      }));

      const processedUserTweets = userTweetsData.map(tweet => ({
        id: tweet.id,
        text: decodeHTMLEntities(tweet.full_text),
        created_at: tweet.date,
        user: {
          name: tweet.user.name,
          screen_name: tweet.user.screen_name,
          profile_image_url: tweet.user.profile_image_url
        },
        url: tweet.url,
        medias: tweet.media,
        source: 'user_tweet',
        retweet_count: tweet.retweet_count,
        reply_count: tweet.reply_count,
        quote_status: tweet.quote_status ? {
          ...tweet.quote_status,
          text: decodeHTMLEntities(tweet.quote_status.full_text)
        } : null
      }));

      const processRedditItem = (item, index = 0) => ({
        id: item.id,
        title: item.title || '',
        text: item.selftext || item.body || '',
        created_at: new Date(item.created_utc * 1000).toISOString(),
        author: item.author?.name || '[deleted]',
        subreddit: item.subreddit.display_name,
        url: `https://new.reddit.com${item.permalink}`,
        ups: item.ups,
        downs: item.downs,
        num_comments: item.num_comments || 0,
        source: 'reddit',
        thumbnail: item.thumbnail,
        is_self: item.is_self,
        over_18: item.over_18,
        // Include media-related fields for filtering
        is_video: item.is_video,
        post_hint: item.post_hint,
        media_metadata: item.media_metadata,
        domain: item.domain,
        // Preserve original image URL for display
        image_url: item.url_overridden_by_dest || item.url,
        _replies: item._replies,
        index: index
      });

      const processedRedditSaves = redditData.saved
        .filter(post => !post.over_18)
        .flatMap((post, postIndex) => {
          const processedPost = processRedditItem(post);
          if (post.comments) {
            const processedComments = post.comments
              .filter(comment => !comment.over_18)
              .map((comment, commentIndex) =>
                processRedditItem(comment, commentIndex + 1)
              );
            return [processedPost, ...processedComments];
          }
          return [processedPost];
        });

      // console.log('Roam data structure:', { roamData: roamData?.slice(0, 3) }); // Show first 3 items
      const processedRoamQuotes = (roamData || [])
        .filter(quote => !quote.skip)
        .map(quote => {
        // Handle both old format (array of [block, page]) and new format (direct quote object)
        if (Array.isArray(quote)) {
          // Old format
          const [block, page] = quote;
          let createdAt;
          try {
            const timestamp = block['create-time'];
            if (timestamp && !isNaN(timestamp)) {
              createdAt = new Date(timestamp).toISOString();
            } else {
              createdAt = new Date().toISOString();
            }
          } catch (error) {
            console.warn('Invalid timestamp in Roam quote:', block[':block/uid'], error);
            createdAt = new Date().toISOString();
          }

          return {
            id: block[':block/uid'],
            text: block[':block/string'],
            created_at: createdAt,
            user: {
              name: page[':node/title'] || 'Roam Research',
              screen_name: 'roam',
              profile_image_url: null
            },
            source: 'roam',
            metadata: {
              page_title: page[':node/title'],
              original_id: block[':block/uid'],
              create_time: block['create-time'],
              edit_time: block['edit-time']
            }
          };
        } else {
          // New format - quote is already an object
          // Use metadata.create_time for proper date sorting
          let createdAt;
          if (quote.metadata?.create_time) {
            createdAt = new Date(quote.metadata.create_time).toISOString();
          } else {
            createdAt = quote.date || quote.updatedAt;
          }
          
          return {
            id: quote.id,
            text: quote.content,
            content: quote.content,
            created_at: createdAt,
            date: quote.date,
            author: quote.author,
            user: {
              name: quote.metadata?.page_title || 'Roam Research',
              screen_name: 'roam',
              profile_image_url: null
            },
            source: quote.source || 'roam',
            contentType: quote.contentType,
            template: quote.template,
            metadata: quote.metadata,
            skip: quote.skip,
            updatedAt: quote.updatedAt
          };
        }
      });

      const mergedData = [...processedLikes, ...processedBookmarks, ...processedUserTweets, ...processedRedditSaves, ...processedRoamQuotes];

      // Process images in dev mode
      if (isDevMode) {
        try {
          if (process.env.REACT_APP_MODE === 'development') {
            console.log('Processing images in dev mode...');
          }
          // Process tweets in batches of 50
          const BATCH_SIZE = 50;
          let processedTweets = [];
          
          for (let i = 0; i < mergedData.length; i += BATCH_SIZE) {
            const batch = mergedData.slice(i, i + BATCH_SIZE);
            // console.log(`Processing batch ${i / BATCH_SIZE + 1} of ${Math.ceil(mergedData.length / BATCH_SIZE)}`);
            
            const response = await fetch('http://localhost:3001/api/dev/process-tweets', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({ tweets: batch }),
            });

            if (!response.ok) {
              throw new Error(`Failed to process batch ${i / BATCH_SIZE + 1}`);
            }

            const { tweets: batchProcessed } = await response.json();
            processedTweets = [...processedTweets, ...batchProcessed];
          }

          if (process.env.REACT_APP_MODE === 'development') {
            console.log('Successfully processed all images');
          }
          // shuffle data on load
          const shuffledData = [...processedTweets].sort(() => 0.5 - Math.random());
          setAllTweets(shuffledData);
        } catch (error) {
          console.warn('Dev server not available, skipping image processing:', error.message);
          console.warn('To enable image processing, run: npm run start:dev');
          // Fall back to unprocessed tweets
          const shuffledData = [...mergedData].sort(() => 0.5 - Math.random());
          setAllTweets(shuffledData);
        }
      } else {
        // In production, just use the merged data
        const shuffledData = [...mergedData].sort(() => 0.5 - Math.random());
        setAllTweets(shuffledData);
      }
      setIsLoading(false);
    } catch (err) {
      console.error('Failed to load data:', err);
      setError(`Failed to load data: ${err.message}`);
      setIsLoading(false);
    }
  }, [isDevMode]);

  // Initial data load
  useEffect(() => {
    let isMounted = true;

    const initialLoad = async () => {
      try {
        if (allTweets.length === 0) {
          await loadTweets();
        }
      } catch (error) {
        if (isMounted) {
          console.error('Failed to load tweets:', error);
          setError(error.message);
        }
      }
    };

    initialLoad();
    return () => {
      isMounted = false;
    };
  }, [allTweets.length, loadTweets]); // Run only once on mount

  // Initialize skip categories when tweets are loaded
  useEffect(() => {
    if (allTweets.length > 0 && isDevMode) {
      initializeFromTweets(allTweets);
    }
  }, [allTweets, isDevMode, initializeFromTweets]);

  const isInsightsPage = location.pathname === '/insights';
  const isMuseumPage = location.pathname === '/museum';
  const isManagePage = location.pathname === '/inkquote';

  if (error) {
    return <div className="text-center text-red-500">{error}</div>;
  }

  const handleTweetUpdate = (updatedTweets) => {
    // Prevent image reprocessing in dev mode - just update the state
    const tweets = Array.isArray(updatedTweets) ? updatedTweets : [updatedTweets];
    setAllTweets(prevTweets => {
      const tweetMap = new Map(tweets.map(t => [t.id, t]));
      return prevTweets.map(tweet => {
        const updatedTweet = tweetMap.get(tweet.id);
        if (!updatedTweet) return tweet;
        // Preserve any existing image processing results
        return {
          ...updatedTweet,
          medias: tweet.medias // Keep existing media data
        };
      });
    });
  };

  return (
    <div className="app-container">
      <div className={`sticky-header-wrapper ${visible ? 'sticky-header-visible' : 'sticky-header-hidden'}`}>
        <div className="container mx-auto max-w-2xl px-4 pt-4">
          <header className="flex flex-col sm:flex-row justify-between items-center mb-4">
            <div className="flex items-center mb-2 sm:mb-0">
              <button
                onClick={toggleDarkMode}
                className="page-icon-toggle text-[#1DA1F2] mr-2"
                aria-label="Toggle theme"
              >
                {isInsightsPage ? (
                  <InsightsIcon size={24} color={darkMode ? "#ffffff" : "#1DA1F2"} />
                ) : isMuseumPage ? (
                  <Building2 size={24} color={darkMode ? "#ffffff" : "#1DA1F2"} />
                ) : isManagePage ? (
                  <SquareDashedKanban size={24} color={darkMode ? "#ffffff" : "#1DA1F2"} />
                ) : (
                  <TwitterIcon size={24} color={darkMode ? "#ffffff" : "#1DA1F2"} />
                )}
              </button>
              <TabbedNavigation />
            </div>
            <div className="flex gap-2 items-center">
              {isDevMode && (
                <>
                  {/* <div className="px-2 py-1 bg-purple-100 text-purple-600 dark:bg-purple-900 dark:text-purple-300 rounded flex items-center gap-1">
                    <Code size={16} />
                    <span className="text-sm">Dev Mode</span>
                  </div> */}
                  <ApiSyncStatus />
                </>
              )}
              <button onClick={toggleDarkMode} className="dark-mode-toggle-desktop">
                {darkMode ? <Sun className="text-yellow-400" /> : <Moon className="text-gray-600" />}
              </button>
            </div>
          </header>
        </div>
      </div>
      <div className="container mx-auto p-4 max-w-2xl">
        <div className="main-content">
          <Routes>
            <Route path="/" element={
              <TwitterSearch
                allTweets={allTweets}
                darkMode={darkMode}
                onTweetUpdate={handleTweetUpdate}
                isInitialLoading={isLoading && allTweets.length === 0}
              />
            } />
            <Route path="/insights" element={<Insights allTweets={allTweets} />} />
            <Route path="/museum" element={<GenerativeMuseum tweets={allTweets} />} />
            <Route path="/inkquote" element={<InkQuoteManager />} />
          </Routes>
        </div>
      </div>
      <StagingTrayIndicator />
      <StagingTrayBottomPanel />
      <PWAInstallPrompt />
      <PWAUpdatePrompt />
    </div>
  );
};

function App() {
  return (
    <Router>
      <ErrorBoundary>
        <DevModeProvider>
          <ToastProvider>
            <FavoritesProvider>
              <SkipCategoriesProvider>
                <StagedEditProvider>
                  <StagingTrayProvider>
                    <AppContent />
                  </StagingTrayProvider>
                </StagedEditProvider>
              </SkipCategoriesProvider>
            </FavoritesProvider>
            <ToastContainer />
          </ToastProvider>
        </DevModeProvider>
      </ErrorBoundary>
    </Router>
  );
}

export default App;